import * as RadixDialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import React, { memo, useEffect, useRef, type ReactNode } from 'react';
import { LegacyIconButton } from './LegacyIconButton';
import { LegacyButton, type LegacyButtonProps } from '~/components/ui/LegacyButton';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { forwardRefMemo } from '~/utils/react';

export { Close as DialogClose } from '@radix-ui/react-dialog';

const transition = {
  duration: 0.15,
  ease: cubicEasingFn,
};

export const dialogBackdropVariants = {
  closed: {
    opacity: 0,
    transition,
  },
  open: {
    opacity: 1,
    transition,
  },
} satisfies Variants;

export const dialogVariants = {
  closed: {
    x: '-50%',
    y: '-40%',
    scale: 0.96,
    opacity: 0,
    transition,
  },
  open: {
    x: '-50%',
    y: '-50%',
    scale: 1,
    opacity: 1,
    transition,
  },
} satisfies Variants;

interface DialogActionsProps {
  children: ReactNode;
  className?: string;
}

/**
 * @deprecated Use `DialogActions` from `~/components/ui/Dialog` instead.
 */
export const DialogActions = forwardRefMemo<HTMLDivElement, DialogActionsProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={classNames('px-5 pb-4 bg-bolt-elements-background-depth-2 flex gap-2 justify-end', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

/**
 * @deprecated Use `DialogButton` from `~/components/ui/Dialog` instead.
 */
export const DialogButton = memo(
  ({ variant, disabled = false, minWidth, children, asChild, onClick, type }: Omit<LegacyButtonProps, 'minHeight'>) => {
    return (
      <LegacyButton
        variant={variant}
        disabled={disabled}
        className="px-4 leading-none focus:outline-none gap-2"
        minHeight={35}
        minWidth={minWidth}
        onClick={onClick}
        asChild={asChild}
        type={type}
      >
        {children}
      </LegacyButton>
    );
  },
);

interface DialogTitleProps extends RadixDialog.DialogTitleProps {
  unstyled?: boolean;
}

/**
 * @deprecated Use `DialogHeader` from `~/components/ui/Dialog` instead.
 */
export const DialogTitle = memo(({ className, unstyled, children, ...props }: DialogTitleProps) => {
  return (
    <RadixDialog.Title
      className={classNames(
        unstyled
          ? undefined
          : 'px-5 py-4 border-b border-bolt-elements-borderColor text-lg font-semibold leading-6 text-bolt-elements-textPrimary',
        className,
      )}
      {...props}
    >
      {children}
    </RadixDialog.Title>
  );
});

interface DialogDescriptionProps extends RadixDialog.DialogDescriptionProps {
  noPadding?: boolean;
  noLinkStyling?: boolean;
  unstyled?: boolean;
}

/**
 * @deprecated Use `DialogContent` from `~/components/ui/Dialog` instead.
 */
export const DialogDescription = forwardRefMemo<HTMLDivElement, DialogDescriptionProps>(
  ({ className, unstyled = false, noLinkStyling = false, noPadding = false, children, ...props }, ref) => {
    return (
      <RadixDialog.Description
        className={classNames(
          {
            'text-bolt-elements-textPrimary text-base': !unstyled,
            'px-5 py-4': !unstyled && !noPadding,

            // style nested links, unless when they have primary background
            ' [&_a]:underline [&_a:not(.bg-bolt-elements-button-primary-background)]:text-bolt-elements-link hover:[&_a:not(.bg-bolt-elements-button-primary-background)]:text-bolt-elements-linkHover':
              !unstyled && !noLinkStyling,
          },
          className,
        )}
        asChild
        {...props}
      >
        <div ref={ref}>{children}</div>
      </RadixDialog.Description>
    );
  },
);

export type DialogIconVariant = 'warning' | 'error';

/**
 * @deprecated Use `badge` prop on `DialogHeader` instead
 */
export const DialogIcon = memo(({ variant }: { variant: DialogIconVariant }) => {
  return (
    <div
      className={classNames('size-12 flex items-center justify-center rounded-full', {
        'bg-bolt-elements-button-warn-background': variant === 'warning',
        'bg-bolt-elements-button-danger-background': variant === 'error',
      })}
    >
      <div
        className={classNames('size-6', {
          'i-ph:warning text-bolt-elements-button-warn-text': variant === 'warning',
          'i-ph:warning text-bolt-elements-button-danger-text': variant === 'error',
        })}
      ></div>
    </div>
  );
});

export interface DialogAlertContentProps {
  icon: DialogIconVariant;
  title: ReactNode;
  description: ReactNode;
  actions?: ReactNode;
}

/**
 * @deprecated Use `badge` prop on `DialogHeader` instead
 */
export const DialogAlertContent = memo(({ title, icon, actions, description }: DialogAlertContentProps) => {
  return (
    <DialogDescription className="flex flex-col gap-6">
      <DialogIcon variant={icon} />

      <DialogTitle unstyled className="text-lg font-semibold leading-6 text-bolt-elements-textPrimary">
        {title}
      </DialogTitle>

      <p className="font-light text-sm text-bolt-elements-textSecondary">{description}</p>

      {actions && <div className="flex flex-col gap-2">{actions}</div>}
    </DialogDescription>
  );
});

/**
 * @deprecated Use `Dialog` from `~/components/ui/Dialog` instead.
 */
export interface DialogProps {
  open: boolean;
  showOverflow?: boolean;
  children: ReactNode | ReactNode[];
  contentClassName?: string;
  backdropClassName?: string;
  unstyled?: boolean;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  width?: string | number;
  height?: string | number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  onClose?: (event: React.UIEvent | KeyboardEvent) => void;
  portalContainer?: Element;

  /**
   * The modality of the dialog. When set to `true`, interaction with outside elements will
   * be disabled and only dialog content will be visible to screen readers.
   *
   * If you provide a `portalContainer` it might make sense to disable the modality.
   */
  modal?: boolean;
}

/**
 * @deprecated Use `Dialog` from `~/components/ui/Dialog` instead.
 */
export const Dialog = memo<DialogProps>(
  ({
    contentClassName,
    backdropClassName,
    open = false,
    width = '90vw',
    height,
    showOverflow = false,
    maxWidth,
    maxHeight = '85vh',
    showCloseButton = true,
    closeOnBackdropClick = true,
    unstyled = false,
    portalContainer,
    children,
    modal,
    onClose,
  }) => {
    const onCloseRef = useRef(onClose);

    useEffect(() => {
      onCloseRef.current = onClose;
    });

    useEffect(() => {
      if (!open) {
        return () => {
          // noop
        };
      }

      const onKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onCloseRef.current?.(event);
        }
      };

      window.addEventListener('keydown', onKeydown);

      return () => {
        window.removeEventListener('keydown', onKeydown);
      };
    }, [open]);

    return (
      <RadixDialog.Root open={open} modal={modal}>
        <AnimatePresence>
          {open && (
            <RadixDialog.Portal forceMount container={portalContainer}>
              <RadixDialog.Overlay onClick={closeOnBackdropClick ? onClose : undefined} asChild>
                <motion.div
                  className={classNames('bg-black/50 fixed inset-0 z-dialog', backdropClassName)}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={dialogBackdropVariants}
                />
              </RadixDialog.Overlay>
              <RadixDialog.Content asChild>
                <motion.div
                  className={classNames(
                    'fixed top-[50%] left-[50%] z-max -translate-x-1/2 -translate-y-1/2 focus:outline-none',
                    unstyled
                      ? undefined
                      : 'border border-bolt-elements-borderColor rounded-lg bg-bolt-elements-background-depth-2 shadow-lg',
                    {
                      'overflow-hidden': !showOverflow,
                    },
                    contentClassName,
                  )}
                  style={{
                    width: typeof width === 'string' ? width : `${width}px`,
                    height: height ? (typeof height === 'string' ? height : `${height}px`) : undefined,
                    maxWidth:
                      typeof maxWidth === 'string'
                        ? maxWidth
                        : typeof maxWidth === 'number'
                          ? `${maxWidth}px`
                          : undefined,
                    maxHeight: typeof maxHeight === 'string' ? maxHeight : `${maxHeight}px`,
                  }}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={dialogVariants}
                >
                  {children}
                  {showCloseButton && (
                    <RadixDialog.Close asChild onClick={onClose}>
                      <LegacyIconButton icon="i-ph:x" className="absolute top-[10px] right-[10px]" />
                    </RadixDialog.Close>
                  )}
                </motion.div>
              </RadixDialog.Content>
            </RadixDialog.Portal>
          )}
        </AnimatePresence>
      </RadixDialog.Root>
    );
  },
);
